import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  IoLocateOutline,
  IoShieldCheckmarkOutline,
  IoLayersOutline,
  IoListOutline,
  IoPlayOutline,
  IoTimerOutline,
} from 'react-icons/io5';
import { api, type CronScheduleRow } from '../api';
import { useAuth } from '../auth';

const CRON_PRESETS = [
  { value: '*/1 * * * *', label: 'Каждую минуту' },
  { value: '*/5 * * * *', label: 'Каждые 5 минут' },
  { value: '*/10 * * * *', label: 'Каждые 10 минут' },
  { value: '*/15 * * * *', label: 'Каждые 15 минут' },
  { value: '*/30 * * * *', label: 'Каждые 30 минут' },
  { value: '0 * * * *', label: 'Каждый час' },
  { value: '0 */3 * * *', label: 'Каждые 3 часа' },
  { value: '0 */6 * * *', label: 'Каждые 6 часов' },
  { value: '0 1 * * *', label: 'Раз в день в 01:00 UTC' },
  { value: '0 2 * * *', label: 'Раз в день в 02:00 UTC' },
  { value: '0 9 * * *', label: 'Раз в день в 09:00 UTC' },
];

export function RepricersPage() {
  const overview = useQuery({ queryKey: ['repricers-overview'], queryFn: api.repricersOverview, refetchInterval: 15_000 });
  const jobs = useQuery({ queryKey: ['repricers-recent-jobs'], queryFn: api.repricersRecentJobs, refetchInterval: 15_000 });
  const schedules = useQuery({ queryKey: ['schedules'], queryFn: api.schedules, refetchInterval: 30_000 });

  if (overview.isLoading) return <div className="card">Загрузка…</div>;
  if (!overview.data) return <div className="card" style={{ color: 'var(--red)' }}>Не удалось загрузить</div>;

  const o = overview.data;

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Репрайсеры</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>Управление автоматизацией цен — обновление каждые 15 сек</div>
        </div>
      </div>

      {/* Top stats */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat">
          <div className="stat-label">BullMQ</div>
          <div className={`stat-value ${o.bullmq.activeCount > 0 ? 'accent' : ''}`}>
            {o.bullmq.activeCount > 0 ? `● ${o.bullmq.activeCount} активн.` : '○ простой'}
          </div>
          <div className="stat-sub">в ожидании: {o.bullmq.waitingCount} · cron: {o.bullmq.repeatable.length}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Наши стратегии</div>
          <div className="stat-value">{o.ourStrategies.length}</div>
          <div className="stat-sub">активных: {o.ourStrategies.filter((s) => s.enabled).length}</div>
        </div>
        <div className="stat">
          <div className="stat-label">РРЦ-защита</div>
          <div className={`stat-value ${o.rrp.enabledProducts > 0 ? 'accent' : ''}`}>
            {o.rrp.enabledProducts}<span style={{ color: 'var(--text-muted)', fontSize: 16 }}>/{o.rrp.availableProducts}</span>
          </div>
          <div className="stat-sub">товаров под защитой</div>
        </div>
        <div className="stat">
          <div className="stat-label">Стратегии Ozon</div>
          <div className="stat-value">{o.ozonStrategies?.length ?? '—'}</div>
          <div className="stat-sub">встроенный репрайсер</div>
        </div>
      </div>

      {/* Schedule editor */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 0' }}>
          <div>
            <h3 style={{ margin: 0 }}>Расписание автоматических задач</h3>
            <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>Cron — UTC. Воркеры подхватывают изменения в течение минуты.</div>
          </div>
        </div>
        <table className="products" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Задача</th>
              <th>Очередь</th>
              <th>Cron</th>
              <th>Статус</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!schedules.data ? <tr><td colSpan={5} className="empty">Загрузка…</td></tr>
              : schedules.data.items.length === 0 ? <tr><td colSpan={5} className="empty">Расписаний нет. Появятся после первого запуска воркеров.</td></tr>
              : schedules.data.items.map((s) => <ScheduleRow key={s.id} s={s} />)
            }
          </tbody>
        </table>
      </div>

      {/* Active now */}
      {o.bullmq.active.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>Сейчас выполняется</h3>
          <table className="products">
            <thead><tr><th>ID</th><th>Задача</th><th>Старт</th><th>Параметры</th></tr></thead>
            <tbody>
              {o.bullmq.active.map((j) => (
                <tr key={j.id}>
                  <td><code>#{j.id}</code></td>
                  <td>{j.name}</td>
                  <td className="muted">{j.startedAt ? new Date(j.startedAt).toLocaleString('ru-RU') : '—'}</td>
                  <td className="muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>{JSON.stringify(j.data).slice(0, 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 3-card grid: Our / Ozon / RRP */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 0' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><IoLocateOutline /> Наши стратегии</h3>
            <Link to="/strategies" className="btn">Все →</Link>
          </div>
          {o.ourStrategies.length === 0 ? <div className="empty" style={{ padding: '32px 20px' }}>Стратегий нет.</div> : (
            <table className="products" style={{ marginTop: 12 }}>
              <thead><tr><th>Название</th><th>Cron</th><th className="num">Товары</th><th>Статус</th></tr></thead>
              <tbody>
                {o.ourStrategies.map((s) => (
                  <tr key={s.id}>
                    <td><Link to={`/strategies/${s.id}`}>{s.name}</Link></td>
                    <td className="muted"><code>{s.scheduleCron}</code></td>
                    <td className="num">{s.productsCount}</td>
                    <td>{s.enabled ? <span className="badge badge-success">● актив</span> : <span className="badge">○ выкл</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 0' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><IoShieldCheckmarkOutline /> РРЦ-защита</h3>
            <Link to="/sync" className="btn">Запуск →</Link>
          </div>
          <div style={{ padding: '12px 20px 20px' }}>
            {o.rrp.availableProducts === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>
                РРЦ ещё не загружена из 1С. После реализации <code>/getRrpPrices</code> в 1С тут появятся товары.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{o.rrp.enabledProducts}</span>
                  <span className="muted">из {o.rrp.availableProducts} товаров под защитой</span>
                </div>
                {o.rrp.lastRun && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    Последний запуск: {new Date(o.rrp.lastRun.startedAt).toLocaleString('ru-RU')}
                    {' · '}
                    {o.rrp.lastRun.status === 'done' ? <span className="badge badge-success">✓ обновлено {o.rrp.lastRun.itemsProcessed}</span> :
                     o.rrp.lastRun.status === 'failed' ? <span className="badge badge-error">✗ ошибка</span> :
                     <span className="badge">{o.rrp.lastRun.status}</span>}
                  </div>
                )}
                <Link to="/products?rrp=set" className="btn" style={{ marginTop: 12 }}>Управлять списком товаров</Link>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 0' }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><IoLayersOutline /> Стратегии Ozon</h3>
            <Link to="/ozon-strategies" className="btn">Все →</Link>
          </div>
          {!o.ozonStrategies ? (
            <div className="empty" style={{ padding: '32px 20px' }}>Ozon API недоступен.</div>
          ) : o.ozonStrategies.length === 0 ? (
            <div className="empty" style={{ padding: '32px 20px' }}>Стратегий нет.</div>
          ) : (
            <table className="products" style={{ marginTop: 12 }}>
              <thead><tr><th>Название</th><th>Тип</th><th className="num">Товары</th><th>Статус</th></tr></thead>
              <tbody>
                {o.ozonStrategies.map((s) => (
                  <tr key={s.id}>
                    <td><Link to="/ozon-strategies">{s.name}</Link></td>
                    <td className="muted" style={{ fontSize: 11 }}>{s.type === 'MIN_EXT_PRICE' ? 'системная' : 'польз.'}</td>
                    <td className="num">{s.products_count}</td>
                    <td>{s.enabled ? <span className="badge badge-success">● актив</span> : <span className="badge">○ выкл</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent jobs */}
      <div className="card">
        <div style={{ padding: '20px 20px 0' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><IoListOutline /> Последние запуски (всех типов)</h3>
        </div>
        {!jobs.data || jobs.data.items.length === 0 ? (
          <div className="empty" style={{ padding: '32px 20px' }}>Запусков ещё не было.</div>
        ) : (
          <table className="products" style={{ marginTop: 12 }}>
            <thead><tr><th>Тип</th><th>Задача</th><th>Старт</th><th>Финиш</th><th>Статус</th><th className="num">Обработано</th><th>Ошибка</th></tr></thead>
            <tbody>
              {jobs.data.items.map((j, i) => (
                <tr key={i}>
                  <td><JobKindBadge kind={j.kind} /></td>
                  <td>{j.label}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{j.startedAt ? new Date(j.startedAt).toLocaleString('ru-RU') : '—'}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{j.finishedAt ? new Date(j.finishedAt).toLocaleString('ru-RU') : '—'}</td>
                  <td>{j.status === 'done' ? <span className="badge badge-success">✓ готово</span> : j.status === 'failed' ? <span className="badge badge-error">✗ ошибка</span> : <span className="badge">{j.status}</span>}</td>
                  <td className="num">{j.updated ?? '—'}</td>
                  <td className="muted" style={{ color: j.error ? 'var(--red)' : undefined, fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.error ?? ''}>
                    {j.error ? j.error.slice(0, 80) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function ScheduleRow({ s }: { s: CronScheduleRow }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editing, setEditing] = useState(false);
  const [pattern, setPattern] = useState(s.pattern);
  const [enabled, setEnabled] = useState(s.enabled);

  const update = useMutation({
    mutationFn: () => api.scheduleUpdate(s.id, { pattern, enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedules'] });
      qc.invalidateQueries({ queryKey: ['repricers-overview'] });
      setEditing(false);
    },
  });

  const runNow = useMutation({
    mutationFn: () => api.scheduleRunNow(s.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['repricers-recent-jobs'] }),
  });

  const toggle = useMutation({
    mutationFn: (next: boolean) => api.scheduleUpdate(s.id, { enabled: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });

  const presetMatch = CRON_PRESETS.find((p) => p.value === s.pattern);

  return (
    <tr>
      <td>
        <div style={{ fontWeight: 500 }}>{s.label ?? s.jobName}</div>
        <div className="muted" style={{ fontSize: 11 }}>{s.id}</div>
      </td>
      <td><span className="badge">{s.queue}</span></td>
      <td>
        {!editing ? (
          <div>
            <code>{s.pattern}</code>
            {presetMatch && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{presetMatch.label}</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
            <select className="input" value={pattern} onChange={(e) => setPattern(e.target.value)}>
              {!CRON_PRESETS.find((p) => p.value === pattern) && <option value={pattern}>{pattern} (кастом)</option>}
              {CRON_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label} — {p.value}</option>)}
            </select>
            <input
              className="input"
              placeholder="custom cron: */15 * * * *"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        )}
      </td>
      <td>
        {!editing ? (
          isAdmin ? (
            <label style={{ cursor: 'pointer', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={s.enabled}
                disabled={toggle.isPending}
                onChange={(e) => toggle.mutate(e.target.checked)}
              />
              {s.enabled ? <span className="badge badge-success">● актив</span> : <span className="badge">○ выкл</span>}
            </label>
          ) : (
            s.enabled ? <span className="badge badge-success">● актив</span> : <span className="badge">○ выкл</span>
          )
        ) : (
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            {enabled ? 'Активно' : 'Выкл.'}
          </label>
        )}
      </td>
      <td>
        {!editing ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {isAdmin && <button className="btn" onClick={() => setEditing(true)}>Изменить</button>}
            <button className="btn" onClick={() => runNow.mutate()} disabled={runNow.isPending}>
              {runNow.isPending ? <IoTimerOutline /> : <><IoPlayOutline /> Запустить</>}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn primary" onClick={() => update.mutate()} disabled={update.isPending}>
              {update.isPending ? 'Сохр…' : 'Сохранить'}
            </button>
            <button className="btn" onClick={() => { setEditing(false); setPattern(s.pattern); setEnabled(s.enabled); }}>Отмена</button>
          </div>
        )}
        {update.error && <div style={{ color: 'var(--red)', fontSize: 11, marginTop: 4 }}>{(update.error as Error).message}</div>}
      </td>
    </tr>
  );
}

function JobKindBadge({ kind }: { kind: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    strategy: { label: 'Стратегия', cls: 'badge-info' },
    rrp_sync: { label: 'РРЦ-синк', cls: 'badge-success' },
    rrp_protect: { label: 'РРЦ-защита', cls: 'badge-warn' },
  };
  const m = map[kind] ?? { label: kind, cls: 'badge' };
  return <span className={`badge ${m.cls}`}>{m.label}</span>;
}
